/**
 * The file itself, rendered.
 *
 * Four fixed lines used to live inline in Settings. They are here now, with
 * everything that has since accumulated around them — see `dossier.ts` for
 * what fills in and when.
 *
 * The one rule this screen follows: it never explains itself. There is no
 * "unlocks in 12 days" anywhere. A redacted row says something exists and
 * declines to say what, which is the same joke the locked trophy rows tell and
 * the same one she tells about herself.
 */

import { TROPHIES, trophyByKey } from '@gramas/shared'
import { Card, ListRow, SectionLabel, Sheet } from '@gramas/ui'
import { useLiveQuery } from 'dexie-react-hooks'
import { listAwards } from '../db/repo.js'
import { daysKnown, FINDS, REDACTED, SAGA_IDS, SAGA_TITLE, SERVICE } from './dossier.js'
import { GlitimeMark } from './Mark.js'
import { listMoments } from './moments.js'
import { glitimeMetAt } from './store.js'

export interface DossierSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function DossierSheet({ open, onOpenChange }: DossierSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange} title="Byla" size="full">
      <DossierBody />
    </Sheet>
  )
}

function DossierBody() {
  const metAt = useLiveQuery(() => glitimeMetAt(), [], null)
  const moments = useLiveQuery(() => listMoments(), [], [])
  const awards = useLiveQuery(() => listAwards(), [], [])

  const known = daysKnown(metAt)
  const found = new Set(moments.filter((m) => m.kind === 'secret').map((m) => m.id))
  const closed = new Map(moments.filter((m) => m.kind === 'saga').map((m) => [m.id, m.on]))
  // Trophies only: the `moment.` rows share this table but are not incidents.
  const incidents = awards.flatMap((row) => {
    const def = trophyByKey(row.awardKey)
    return def == null ? [] : [{ def, on: row.awardedOn }]
  })

  return (
    <div className="flex flex-col gap-1 pb-6">
      <div className="flex flex-col items-center gap-2 pt-1 pb-3">
        <GlitimeMark size={52} alive station="zone" />
        <p className="font-display font-semibold text-ink text-lg">Glitimė</p>
      </div>

      <SectionLabel>Tarnybos laikas</SectionLabel>
      <Card className="mx-4">
        {SERVICE.map((entry, i) => {
          const open = known >= entry.after
          return (
            <ListRow
              key={entry.label}
              last={i === SERVICE.length - 1}
              title={entry.label}
              subtitle={open ? entry.value : REDACTED}
              className={open ? undefined : 'opacity-55'}
            />
          )
        })}
      </Card>

      <SectionLabel>
        Incidentai · {incidents.length} iš {TROPHIES.length}
      </SectionLabel>
      <Card className="mx-4">
        {incidents.length === 0 ? (
          <ListRow title="Įrašų nėra" subtitle="Byla kol kas švari" />
        ) : (
          incidents.map((entry, i) => (
            <ListRow
              key={entry.def.key}
              last={i === incidents.length - 1}
              title={entry.def.title}
              subtitle={entry.def.description}
              value={entry.on}
            />
          ))
        )}
      </Card>

      <SectionLabel>
        Bylos · {closed.size} iš {SAGA_IDS.length}
      </SectionLabel>
      <Card className="mx-4">
        {SAGA_IDS.map((id, i) => {
          const on = closed.get(id)
          return (
            <ListRow
              key={id}
              last={i === SAGA_IDS.length - 1}
              title={on == null ? REDACTED : (SAGA_TITLE[id] ?? id)}
              subtitle={on == null ? 'Nagrinėjama' : 'Uždaryta'}
              value={on}
              className={on == null ? 'opacity-55' : undefined}
            />
          )
        })}
      </Card>

      <SectionLabel>
        Radiniai · {found.size} iš {FINDS.length}
      </SectionLabel>
      <Card className="mx-4">
        {FINDS.map((find, i) => {
          const got = found.has(find.id)
          return (
            <ListRow
              key={find.id}
              last={i === FINDS.length - 1}
              title={got ? find.label : REDACTED}
              subtitle={find.when}
              className={got ? undefined : 'opacity-55'}
            />
          )
        })}
      </Card>
    </div>
  )
}
